import axiosInstance from './axios'
import {
  clearTokens,
  expiresInSeconds,
  getTokens,
  setTokens
} from './jwtStorage'

const REFRESH_BEFORE_EXPIRY_SECONDS = 300

let refreshHandle: ReturnType<typeof setTimeout> | null = null
let inflightRefresh: Promise<void> | null = null

export const cancelRefresh = (): void => {
  if (refreshHandle !== null) {
    clearTimeout(refreshHandle)
    refreshHandle = null
  }
}

const expireAuth = (): void => {
  cancelRefresh()
  clearTokens()
  window.location.replace('/login')
}

const _doRefresh = async (): Promise<void> => {
  const tokens = getTokens()
  if (tokens === null) {
    expireAuth()
    return
  }

  try {
    const response = await axiosInstance.post('/auth/refresh-token', {
      refreshToken: tokens.refreshToken
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = response.data as any

    if (response.status === 200 && data?.accessToken && data?.expiresAt) {
      setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || tokens.refreshToken,
        expiresAt: data.expiresAt
      })
      scheduleRefresh()
    } else {
      expireAuth()
    }
  } catch {
    expireAuth()
  }
}

export const refreshAccessToken = (): Promise<void> => {
  if (inflightRefresh !== null) return inflightRefresh
  inflightRefresh = _doRefresh().finally(() => {
    inflightRefresh = null
  })
  return inflightRefresh
}

export const scheduleRefresh = (): void => {
  cancelRefresh()

  const secondsRemaining = expiresInSeconds()
  const delayMs = (secondsRemaining - REFRESH_BEFORE_EXPIRY_SECONDS) * 1000

  if (delayMs <= 0) {
    void refreshAccessToken().catch(() => undefined)
  } else {
    refreshHandle = setTimeout(() => {
      void refreshAccessToken().catch(() => undefined)
    }, delayMs)
  }
}

export const startMultiTabSync = (): void => {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== 'goldie:auth:accessToken') return

    if (event.newValue !== null) {
      // Another tab updated the token — reschedule refresh with the new expiry
      cancelRefresh()
      scheduleRefresh()
    } else {
      // Another tab cleared the tokens — mirror the logout
      clearTokens()
      window.location.replace('/login')
    }
  })
}
